<template>
  <mtd-aside ref="menuRef" class="mc-aside" style="width: auto">
    <div class="mc-sidebar mtd-menu-dark" :style="activeSideBarStyle">
      <slot name="top"></slot>
      <mtd-menu
        v-model="activeId"
        class="mc-sidebar-menu"
        :mode="innerMode"
        theme="dark"
        v-bind="$attrs"
        @select="getActiveMenu"
      >
        <div v-for="item in sourceData" :key="item.id">
          <mtd-submenu
            v-if="item.children && item.children.length"
            :name="item.id"
            :icon="item.icon ? item.icon : ''"
          >
            <template #title>
              <mtd-tooltip :content="item.title" placement="top">
                <span>{{ item.title }}</span>
              </mtd-tooltip>
            </template>
            <template #icon>
              <img :src="item.iconUrl" class="custom-icon" />
            </template>
            <mtd-menu-item
              v-for="child in item.children"
              :key="child.id"
              :name="child.url"
              :icon="child.icon"
              :disabled="child.disabled"
            >
              <mtd-tooltip :content="child.title" placement="top">
                <span>{{ child.title }}</span>
              </mtd-tooltip>
            </mtd-menu-item>
          </mtd-submenu>
          <mtd-menu-item
            v-else
            :key="item.id"
            :name="item.url"
            :icon="item.icon"
            :disabled="item.disabled"
          >
            <mtd-tooltip :content="item.title" placement="top">
              <span>{{ item.title }}</span>
            </mtd-tooltip>
          </mtd-menu-item>
        </div>
      </mtd-menu>
    </div>
    <mtd-tooltip
      :content="isCollapse ? '展开侧边栏' : '折叠侧边栏'"
      placement="right"
      :show-arrow="false"
    >
      <div
        class="mc-aside-toggle"
        :style="activeLeftStyle"
        @click="collapseSidebar"
      ></div>
    </mtd-tooltip>
  </mtd-aside>
</template>

<script>
/**
 * MCMenu 组件，侧边栏菜单组件
 *
 * @props {string} value - 当前激活菜单的 ID
 * @props {string} mode - 菜单模式，可选值：'inline'、'horizontal'、'vertical'
 * @props {Array<Object>} source - 菜单数据源
 * @props {Object} menuWidth - 菜单宽度，包含 collapsedWidth 和 originalWidth
 * @props {Boolean} collapseStatus - 菜单折叠状态
 * @props 继承 mtd-menu 的 props
 * @emits {void} loaded - 菜单加载完成
 * @emits {Object, string} selected - 菜单项被选中时触发，返回选中菜单项和其 ID
 */
import {
  Aside as MtdAside,
  Menu as MtdMenu,
  Submenu as MtdSubmenu,
  MenuItem as MtdMenuItem,
  Tooltip as MtdTooltip,
} from "@ss/mtd-vue";
import { setStorageWithoutRegion, getStorageWithoutRegion } from "@osgfe/tools-storage";

/**
 * 根据菜单项生成完整 URL
 * @param {Object} menu - 菜单项对象
 * @returns {string|null} 完整 URL 或 null
 */
const getUrl = (menu) => {
  if (!menu.target) {
    return null;
  }
  let pathname = menu.url || "";
  if (!pathname.endsWith(".html")) {
    pathname = `${pathname}.html`;
  }
  if (!pathname.startsWith("/")) {
    pathname = `/${pathname}`;
  }
  return `${window.location.origin}${pathname}`;
};

/**
 * 根据 URL 查找对应菜单项
 * @param {Array<Object>} menuList - 菜单列表
 * @param {string} url - 要查找的 URL
 * @returns {Object} 找到的菜单项或空对象
 */
const findMenuByPath = (menuList, url) => {
  for (const menu of menuList) {
    if (menu.url === url) {
      return menu;
    }
    if (menu.children) {
      const targetChild = menu.children.find((child) => child.url === url);
      if (targetChild) {
        return targetChild;
      }
    }
  }
  return {};
};

export default {
  name: "MCMenu",
  components: {
    MtdAside,
    MtdMenu,
    MtdSubmenu,
    MtdMenuItem,
    MtdTooltip,
  },
  props: {
    value: {
      type: String,
      default: "",
    },
    mode: {
      type: String,
      default: "inline",
    },
    source: {
      type: Array,
      required: true,
    },
    menuWidth: {
      type: Object,
      default() {
        return {
          collapsedWidth: "56px",
          originalWidth: "220px",
        };
      },
    },
    collapseStatus: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      menuRef: null,
      isCollapse: false,
      activeId: "",
      getUrl: getUrl,
    };
  },
  computed: {
    sourceData() {
      return this.source;
    },
    innerMode() {
      return this.mode;
    },
    activeSideBarStyle() {
      const isHidden =
        this.isCollapse && this.menuWidth.collapsedWidth === "0px";
      return {
        width: this.isCollapse
          ? this.menuWidth.collapsedWidth
          : this.menuWidth.originalWidth,
        display: isHidden ? "none" : "",
      };
    },
    activeLeftStyle() {
      return {
        left: this.isCollapse
          ? this.menuWidth.collapsedWidth
          : this.menuWidth.originalWidth,
      };
    },
  },
  watch: {
    collapseStatus(newVal) {
      this.setCollapseStatus(newVal);
    },
    value(newVal) {
      this.setActiveMenu(newVal);
    },
  },
  mounted() {
    this.$emit("loaded");
    // 初始化
    this.setCollapseStatus(this.collapseStatus);
    this.setActiveMenu(this.value);
  },
  methods: {
    getCollapseStatus() {
      const historyStatus = getStorageWithoutRegion("isCollapse");
      if (historyStatus) {
        return historyStatus === "true" ? true : false;
      } else {
        return this.isCollapse;
      }
    },
    setCollapseStatus(status) {
      this.isCollapse = status;
      setStorageWithoutRegion("isCollapse", `${this.isCollapse}`);
    },
    getActiveMenu(menuItem) {
      // name属性值为菜单url
      const selectedMenuPath = menuItem?.name;
      const activeMenuItem = findMenuByPath(this.source, selectedMenuPath);
      this.$emit("selected", activeMenuItem);
      return activeMenuItem;
    },
    setActiveMenu(activeId) {
      this.activeId = activeId;
      let activeName = activeId;
      if (
        window &&
        window.top &&
        window.top.location &&
        window.top.location.search
      ) {
        activeName = `${window.top.location.pathname}${window.top.location.search}`;
      }
      setStorageWithoutRegion("activeName", activeName);
    },
    collapseSidebar() {
      this.setCollapseStatus(!this.isCollapse);
    },
  },
};
</script>

<style scoped>
:deep() .mtdicon.mtdicon-down-thick.mtd-submenu-direction {
  margin-inline-start: 2px;
}

.custom-icon {
  width: 20px;
  height: 20px;
}
</style>
